import { undo, redo } from '@codemirror/commands';

/**
 * 선택 텍스트를 래퍼로 감싸기/해제 (토글)
 */
export function toggleWrapper(view, wrapper) {
  const { state } = view;
  const { from, to } = state.selection.main;
  const selectedText = state.doc.sliceString(from, to);

  if (
    selectedText.startsWith(wrapper) &&
    selectedText.endsWith(wrapper) &&
    selectedText.length >= wrapper.length * 2
  ) {
    view.dispatch({
      changes: { from, to, insert: selectedText.slice(wrapper.length, -wrapper.length) },
    });
  } else {
    view.dispatch({
      changes: { from, to, insert: `${wrapper}${selectedText}${wrapper}` },
    });
  }
  view.focus();
}

/**
 * 줄 앞에 접두사 추가/제거 (토글)
 */
function toggleLinePrefix(view, prefix) {
  const { state } = view;
  const { from, to } = state.selection.main;
  const startLine = state.doc.lineAt(from);
  const endLine = state.doc.lineAt(to);
  const changes = [];

  for (let i = startLine.number; i <= endLine.number; i++) {
    const line = state.doc.line(i);
    const text = line.text;

    if (text.startsWith(prefix)) {
      changes.push({ from: line.from, to: line.from + prefix.length, insert: '' });
    } else {
      // Remove other heading/list prefixes first
      const cleaned = text.replace(/^(#{1,6}\s|>\s|[-*+]\s(\[[ x]\]\s)?|\d+\.\s)/, '');
      changes.push({ from: line.from, to: line.from + (text.length - cleaned.length), insert: prefix });
    }
  }

  if (changes.length) {
    view.dispatch({ changes });
  }
  view.focus();
}

/**
 * 줄 앞 접두사를 특정 값으로 설정 (다른 접두사 교체)
 */
function setLinePrefix(view, prefix) {
  const { state } = view;
  const { from } = state.selection.main;
  const line = state.doc.lineAt(from);
  const text = line.text;

  // Check if already has this prefix
  if (text.startsWith(prefix)) {
    // Remove it (toggle off → plain text)
    view.dispatch({
      changes: { from: line.from, to: line.from + prefix.length, insert: '' },
    });
  } else {
    // Remove existing prefix and add new one
    const match = text.match(/^(#{1,6}\s|>\s|```\s?)/);
    const existingLen = match ? match[0].length : 0;
    view.dispatch({
      changes: { from: line.from, to: line.from + existingLen, insert: prefix },
    });
  }
  view.focus();
}

// === 문단 스타일 ===
export function setHeading(view, level) {
  const prefix = '#'.repeat(level) + ' ';
  setLinePrefix(view, prefix);
}

export function setParagraph(view) {
  const { state } = view;
  const { from } = state.selection.main;
  const line = state.doc.lineAt(from);
  const match = line.text.match(/^(#{1,6}\s|>\s|```\s?)/);
  if (match) {
    view.dispatch({
      changes: { from: line.from, to: line.from + match[0].length, insert: '' },
    });
  }
  view.focus();
}

export function toggleBlockquote(view) {
  toggleLinePrefix(view, '> ');
}

export function toggleCodeBlock(view) {
  const { state } = view;
  const { from, to } = state.selection.main;
  const selectedText = state.doc.sliceString(from, to);

  // Check if cursor is inside a code block
  const line = state.doc.lineAt(from);
  const text = line.text.trimStart();
  if (text.startsWith('```')) {
    // Remove code block markers - find the block bounds
    return;
  }

  // Wrap selection in code block
  const insert = selectedText
    ? '```\n' + selectedText + '\n```'
    : '```\n\n```';

  view.dispatch({
    changes: { from, to, insert },
    selection: { anchor: from + 4 },
  });
  view.focus();
}

// === 텍스트 서식 ===
export function toggleBold(view) { toggleWrapper(view, '**'); }
export function toggleItalic(view) { toggleWrapper(view, '*'); }
export function toggleStrikethrough(view) { toggleWrapper(view, '~~'); }
export function toggleInlineCode(view) { toggleWrapper(view, '`'); }

// HTML 태그 래퍼 토글 (<tag>text</tag>)
function toggleHtmlTag(view, tag) {
  const { state } = view;
  const { from, to } = state.selection.main;
  const selectedText = state.doc.sliceString(from, to);
  const open = `<${tag}>`;
  const close = `</${tag}>`;

  if (selectedText.startsWith(open) && selectedText.endsWith(close)) {
    view.dispatch({
      changes: { from, to, insert: selectedText.slice(open.length, -close.length) },
    });
  } else {
    view.dispatch({
      changes: { from, to, insert: `${open}${selectedText}${close}` },
      selection: { anchor: from + open.length, head: to + open.length },
    });
  }
  view.focus();
}

export function toggleUnderline(view) { toggleHtmlTag(view, 'u'); }
export function toggleSubscript(view) { toggleHtmlTag(view, 'sub'); }
export function toggleSuperscript(view) { toggleHtmlTag(view, 'sup'); }

export function insertTextColor(view, color) {
  const { state } = view;
  const { from, to } = state.selection.main;
  const selectedText = state.doc.sliceString(from, to);

  // Check if already wrapped with color span — remove it
  const colorMatch = selectedText.match(/^<span style="color:\s*[^"]+">(.+?)<\/span>$/s);
  if (colorMatch) {
    view.dispatch({ changes: { from, to, insert: colorMatch[1] } });
  } else {
    const text = selectedText || '텍스트';
    const insert = `<span style="color: ${color}">${text}</span>`;
    view.dispatch({
      changes: { from, to, insert },
      selection: { anchor: from + insert.length },
    });
  }
  view.focus();
}

export function insertStatusLabel(view, status = '진행중') {
  const { state } = view;
  const { from, to } = state.selection.main;
  const insert = `**[${status}]**`;
  view.dispatch({
    changes: { from, to, insert },
    selection: { anchor: from + insert.length },
  });
  view.focus();
}

export function insertCaption(view, captionText, imageFrom, imageTo) {
  const { state } = view;
  // Insert <figcaption> right after the image
  const pos = imageTo || state.selection.main.to;
  const insert = `\n<figcaption>${captionText || '캡션'}</figcaption>`;
  view.dispatch({
    changes: { from: pos, to: pos, insert },
    selection: { anchor: pos + insert.length },
  });
  view.focus();
}

export function clearFormatting(view) {
  const { state } = view;
  const { from, to } = state.selection.main;
  if (from === to) { view.focus(); return; }

  let text = state.doc.sliceString(from, to);
  // Remove inline markers
  text = text.replace(/\*\*(.+?)\*\*/g, '$1');
  text = text.replace(/\*(.+?)\*/g, '$1');
  text = text.replace(/~~(.+?)~~/g, '$1');
  text = text.replace(/`(.+?)`/g, '$1');
  // Remove line prefixes
  text = text.replace(/^(#{1,6}\s|>\s|[-*+]\s(\[[ x]\]\s)?|\d+\.\s)/gm, '');

  view.dispatch({ changes: { from, to, insert: text } });
  view.focus();
}

// === 목록 ===
export function toggleBulletList(view) { toggleLinePrefix(view, '- '); }
export function toggleOrderedList(view) {
  const { state } = view;
  const { from, to } = state.selection.main;
  const startLine = state.doc.lineAt(from);
  const endLine = state.doc.lineAt(to);
  const changes = [];

  let num = 1;
  for (let i = startLine.number; i <= endLine.number; i++) {
    const line = state.doc.line(i);
    const text = line.text;
    const prefix = `${num}. `;

    if (/^\d+\.\s/.test(text)) {
      // Already ordered — remove
      const match = text.match(/^\d+\.\s/);
      changes.push({ from: line.from, to: line.from + match[0].length, insert: '' });
    } else {
      // Remove other prefixes, add numbered
      const cleaned = text.replace(/^(#{1,6}\s|>\s|[-*+]\s(\[[ x]\]\s)?|\d+\.\s)/, '');
      changes.push({ from: line.from, to: line.from + (text.length - cleaned.length), insert: prefix });
      num++;
    }
  }
  if (changes.length) view.dispatch({ changes });
  view.focus();
}
export function toggleCheckList(view) { toggleLinePrefix(view, '- [ ] '); }

// === 들여쓰기 ===
export function indent(view) {
  const { state } = view;
  const { from, to } = state.selection.main;
  const startLine = state.doc.lineAt(from);
  const endLine = state.doc.lineAt(to);
  const changes = [];

  for (let i = startLine.number; i <= endLine.number; i++) {
    const line = state.doc.line(i);
    changes.push({ from: line.from, insert: '  ' });
  }
  view.dispatch({ changes });
  view.focus();
}

export function outdent(view) {
  const { state } = view;
  const { from, to } = state.selection.main;
  const startLine = state.doc.lineAt(from);
  const endLine = state.doc.lineAt(to);
  const changes = [];

  for (let i = startLine.number; i <= endLine.number; i++) {
    const line = state.doc.line(i);
    const match = line.text.match(/^( {1,2}|\t)/);
    if (match) {
      changes.push({ from: line.from, to: line.from + match[0].length, insert: '' });
    }
  }
  if (changes.length) view.dispatch({ changes });
  view.focus();
}

// === 삽입 커맨드 ===

export function insertLink(view, text, url, posOverride) {
  const { state } = view;
  const { from, to } = posOverride || state.selection.main;
  const selected = state.doc.sliceString(from, to);
  const linkText = text || selected || 'link';
  const insert = `[${linkText}](${url || ''})`;
  view.dispatch({
    changes: { from, to, insert },
    selection: { anchor: from + insert.length },
  });
  view.focus();
}

export function insertImage(view, alt, url, posOverride) {
  const { state } = view;
  const { from, to } = posOverride || state.selection.main;
  const insert = `![${alt || ''}](${url || ''})`;
  view.dispatch({
    changes: { from, to, insert },
    selection: { anchor: from + insert.length },
  });
  view.focus();
}

export function insertTable(view, rows, cols) {
  const { state } = view;
  const { from } = state.selection.main;

  const headerCells = Array.from({ length: cols }, (_, i) => ` Col ${i + 1} `);
  const sepCells = Array.from({ length: cols }, () => '-------');
  const emptyCells = Array.from({ length: cols }, () => '       ');

  let table = '|' + headerCells.join('|') + '|\n';
  table += '|' + sepCells.join('|') + '|\n';
  for (let r = 0; r < rows; r++) {
    table += '|' + emptyCells.join('|') + '|\n';
  }

  // Ensure newline before table
  const line = state.doc.lineAt(from);
  const prefix = line.text.trim() ? '\n' : '';

  view.dispatch({
    changes: { from, to: from, insert: prefix + table },
    selection: { anchor: from + prefix.length + table.indexOf('Col 1') },
  });
  view.focus();
}

export function insertHorizontalRule(view) {
  const { state } = view;
  const { from } = state.selection.main;
  const line = state.doc.lineAt(from);
  const prefix = line.text.trim() ? '\n' : '';
  view.dispatch({
    changes: { from, to: from, insert: prefix + '---\n' },
  });
  view.focus();
}

export function insertDate(view) {
  const { state } = view;
  const { from, to } = state.selection.main;
  const now = new Date();
  const kst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
  const date = kst.toISOString().split('T')[0];
  view.dispatch({ changes: { from, to, insert: date } });
  view.focus();
}

export function insertTOC(view) {
  const { state } = view;
  const { from } = state.selection.main;
  // Generate TOC from existing headings
  const lines = state.doc.toString().split('\n');
  const tocLines = ['## 목차\n'];
  lines.forEach(l => {
    const m = l.match(/^(#{1,6})\s+(.+)/);
    if (m) {
      const level = m[1].length;
      const text = m[2];
      const indent = '  '.repeat(level - 1);
      const anchor = text.toLowerCase().replace(/[^\w가-힣]+/g, '-').replace(/^-|-$/g, '');
      tocLines.push(`${indent}- [${text}](#${anchor})`);
    }
  });
  if (tocLines.length === 1) {
    tocLines.push('- (제목 없음)');
  }
  const line = state.doc.lineAt(from);
  const prefix = line.text.trim() ? '\n' : '';
  view.dispatch({
    changes: { from, to: from, insert: prefix + tocLines.join('\n') + '\n\n' },
  });
  view.focus();
}

export function insertCallout(view, type = 'INFO') {
  const { state } = view;
  const { from } = state.selection.main;
  const line = state.doc.lineAt(from);
  const prefix = line.text.trim() ? '\n' : '';
  const insert = `${prefix}> [!${type}]\n> \n`;
  view.dispatch({
    changes: { from, to: from, insert },
    selection: { anchor: from + prefix.length + insert.indexOf('> \n') + 2 },
  });
  view.focus();
}

export function insertText(view, text, posOverride) {
  const { state } = view;
  const { from, to } = posOverride || state.selection.main;
  view.dispatch({
    changes: { from, to, insert: text },
    selection: { anchor: from + text.length },
  });
  view.focus();
}

// === Undo / Redo ===
export function doUndo(view) { undo(view); view.focus(); }
export function doRedo(view) { redo(view); view.focus(); }

// === 커서 위치 기반 활성 포맷 감지 ===
export function getActiveFormats(state) {
  if (!state) return {};
  const pos = state.selection.main.head;
  const line = state.doc.lineAt(pos);
  const text = line.text;

  // Heading level
  const headingMatch = text.match(/^(#{1,6})\s/);
  const headingLevel = headingMatch ? headingMatch[1].length : 0;

  // Check inline formats around cursor position
  const lineOffset = pos - line.from;
  const lineText = text;

  // Simple heuristic: check if cursor is within markers
  const beforeCursor = lineText.slice(0, lineOffset);
  const afterCursor = lineText.slice(lineOffset);

  const isBold = (beforeCursor.match(/\*\*/g) || []).length % 2 === 1 &&
                 (afterCursor.match(/\*\*/g) || []).length >= 1;
  const isItalicOnly = !isBold &&
                       (beforeCursor.match(/\*/g) || []).length % 2 === 1 &&
                       (afterCursor.match(/\*/g) || []).length >= 1;
  const isStrikethrough = (beforeCursor.match(/~~/g) || []).length % 2 === 1 &&
                          (afterCursor.match(/~~/g) || []).length >= 1;
  const isCode = (beforeCursor.match(/`/g) || []).length % 2 === 1 &&
                 (afterCursor.match(/`/g) || []).length >= 1;

  return {
    heading: headingLevel,
    bold: isBold,
    italic: isItalicOnly,
    strikethrough: isStrikethrough,
    inlineCode: isCode,
    blockquote: /^\s*>\s/.test(text),
    bulletList: /^\s*[-*+]\s/.test(text) && !/^\s*[-*+]\s\[[ x]\]/.test(text),
    orderedList: /^\s*\d+\.\s/.test(text),
    checkList: /^\s*[-*+]\s\[[ x]\]\s/.test(text),
  };
}
